import React, { useEffect, useState } from 'react';
import DashboardLayout from '../dashboardLayout';
import './floor-plan.css';
import {
	updateMasjidApi,
	getMasjidByIdApi,
} from '../../../api/adminApi';
import { validateMasjidData } from '../../../types/masjid.types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, TextField, Select } from '@mui/material';
import FloorPlanBuilder from '../../../components/common/FloorPlanBuilder';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { createFloorValidateField } from '../../../helpers/validateField';
import { adminSelector, getFloorPlanListsById } from '../../../redux/slicers/adminSlice';
import { errorToast } from '../../../helpers/projectHelper';

const EditFloorPlan = () => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { floorPlanListsById } = useSelector(adminSelector);
	const [loadingBtn, setLoadingBtn] = useState(false);
	const [isSeatLayoutChanged, setIsSeatLayoutChanged] = useState(false);
	const [canAssignSeat, setCanAssignSeat] = useState(false);

	const [formData, setFormData] = useState({
		masjidName: '',
		cityName: '',
		masjidPicture: '',
		floorName: '',
		floorCode: 'G',
		rowCount: 3,
		columnCount: 3,
	});

	const [errors, setErrors] = useState({});
	const [floorErr, setFloorErr] = useState({});
	const [floorData, setFloorData] = useState({ floors: [] });

	useEffect(() => {
		dispatch(getMasjidByIdApi(
			id,
			() => { },
			(data) => dispatch(getFloorPlanListsById(data)),
			() => { }
		));
	}, [dispatch, id]);

	useEffect(() => {
		if (floorPlanListsById && Object.keys(floorPlanListsById).length) {
			setFormData({
				masjidName: floorPlanListsById.name || '',
				cityName: floorPlanListsById.city || '',
				masjidPicture: floorPlanListsById.masjidPicture || '',
				floorName: '',
				floorCode: 'G',
				rowCount: 3,
				columnCount: 3,
			});
			setFloorData({ floors: floorPlanListsById.floorPlanTemplate?.floors || [] });
			setCanAssignSeat(false);
		}
	}, [floorPlanListsById]);

	const handleChange = (event) => {
		const { name, value, type, checked } = event.target;
		const fieldValue = type === 'checkbox' ? checked : value;
		const fieldError = createFloorValidateField(name, fieldValue);

		setFormData((prev) => ({ ...prev, [name]: fieldValue }));
		if (fieldError) {
			setErrors({ [name]: fieldError });
		} else {
			setErrors({});
		}
	};

	function generateFloor() {
		if (canAssignSeat) {
			errorToast('Cannot update the seats');
			return;
		}
		setIsSeatLayoutChanged(true);
		if (formData.floorName) {
			setFloorErr((prev) => ({ ...prev, floorName: '' }));
			const newFloor = {
				floorName: formData.floorName,
				rows: Array.from({ length: formData.rowCount }, (_, i) => ({
					rowName: formData.floorName.charAt(0) + getRowName(i),
					seats: Array.from({ length: formData.columnCount }, (_, j) => ({
						number: String(j + 1),
						isReservedForSpecialGuest: false,
						notes: ''
					})),
				})),
				capacity: formData.rowCount * formData.columnCount,
			};

			function getRowName(index) {
				const baseChar = 65;
				let rowName = '';
				while (index >= 0) {
					const charCode = baseChar + (index % 26);
					rowName = String.fromCharCode(charCode) + rowName;
					index = Math.floor(index / 26) - 1;
				}
				return rowName;
			}

			setFloorData((prev) => ({
				...prev,
				floors: [...(prev.floors || []), newFloor],
			}));
			setFormData((prev) => ({
				...prev,
				floorName: '',
				rowCount: 3,
				columnCount: 3,
			}));
		} else {
			setFloorErr((prev) => ({ ...prev, floorName: 'Floor Name is required' }));
		}
	}

	function handleModifyRow(floorIndex, updatedRows) {
		if (canAssignSeat) {
			errorToast('Cannot update the seats');
			return;
		}
		setIsSeatLayoutChanged(true);
		setFloorData((prev) => {
			const updatedFloors = prev.floors.map((floor, i) => {
				if (i === floorIndex) {
					return {
						...floor,
						rows: updatedRows,
						capacity: updatedRows.reduce(
							(total, row) => total + row.seats.length,
							0
						),
					};
				}
				return floor;
			});
			return { ...prev, floors: updatedFloors };
		});
	}

	function handleSubmit() {
		const formErrors = {};

		// Only validate masjid fields, not floor creation fields
		const fieldsToValidate = ['masjidName', 'cityName'];
		fieldsToValidate.forEach((name) => {
			const value = formData[name];
			const fieldError = createFloorValidateField(name, value);
			if (fieldError) {
				formErrors[name] = fieldError;
			}
		});

		setErrors(formErrors);
		if (Object.keys(formErrors).length === 0) {
			const reqBody = {
				name: formData.masjidName,
				city: formData.cityName,
				masjidPicture: formData.masjidPicture,
				...(isSeatLayoutChanged && {
					floorPlanTemplate: {
						floors: floorData.floors,
					},
				}),
			};

			const validation = validateMasjidData(reqBody);

			if (!validation.isValid) {
				errorToast(validation.errors.join(', '));
				return;
			}

			dispatch(
				updateMasjidApi(
					id,
					reqBody,
					setLoadingBtn,
					() => {
						navigate(-1);
					},
					() => {
						setLoadingBtn(false);
					}
				)
			);
		} else {
			console.log('Validation errors:', formErrors);
		}
	}

	return (
		<DashboardLayout>
			<div className='floor-map' style={{ maxWidth: '1080px' }}>
				<div className='controls'>
					<div className='back-btn-wrap'>
						<Button
							variant='contained'
							className='back-btn'
							onClick={() => navigate(-1)}
						>
							<i className='fa-solid fa-arrow-left'></i> Back
						</Button>
					</div>
					<h1>Edit Masjid Details</h1>
					<br />
					<div className='form-group-control fields'>
						<div className='form-group w50'>
							<TextField
								type='text'
								variant='outlined'
								label='Masjid Name:'
								name='masjidName'
								value={formData.masjidName}
								onChange={handleChange}
							/>
							{errors.masjidName && (
								<p className='error-msg'>{errors.masjidName}</p>
							)}
						</div>
						<div className='form-group w50'>
							<TextField
								type='text'
								variant='outlined'
								label='City Name:'
								name='cityName'
								value={formData.cityName}
								onChange={handleChange}
							/>
							{errors.cityName && (
								<p className='error-msg'>{errors.cityName}</p>
							)}
						</div>
					</div>
					<div className='form-group-control fields'>
						<div className='form-group w50'>
							<TextField
								type='text'
								variant='outlined'
								label='Masjid Picture URL (Optional):'
								name='masjidPicture'
								value={formData.masjidPicture}
								onChange={handleChange}
							/>
						</div>
					</div>
					<hr
						style={{
							width: '100%',
							backgroundColor: 'black',
							fontWeight: 'bold',
						}}
					/>
					<h1>Masjid Floor Info</h1>
					<br />
					<div className='form-group-control fields'>
						<div className='form-group w100'>
							<TextField
								variant='outlined'
								type='text'
								label='Floor Name:'
								name='floorName'
								value={formData.floorName}
								onChange={handleChange}
							/>
							{floorErr.floorName && (
								<p className='error-msg'>{floorErr.floorName}</p>
							)}
						</div>
						<div className='form-group w50 select'>
							<FormControl fullWidth>
								<InputLabel id='floor-code-label'>
									Floor Code
								</InputLabel>
								<Select
									labelId='floor-code-label'
									id='floor-code-select'
									value={formData.floorCode}
									name='floorCode'
									label='Floor Code'
									onChange={handleChange}
								>
									<MenuItem value='G'>G - Ground Floor</MenuItem>
									<MenuItem value='F1'>F1 - First Floor</MenuItem>
									<MenuItem value='F2'>F2 - Second Floor</MenuItem>
									<MenuItem value='F3'>F3 - Third Floor</MenuItem>
									<MenuItem value='B1'>B1 - Basement 1</MenuItem>
									<MenuItem value='B2'>B2 - Basement 2</MenuItem>
								</Select>
							</FormControl>
						</div>
						<div className='form-group w50'>
							<TextField
								type='number'
								variant='outlined'
								label='Number of Rows:'
								name='rowCount'
								value={formData.rowCount}
								onChange={handleChange}
							/>
							{errors.rowCount && (
								<p className='error-msg'>{errors.rowCount}</p>
							)}
						</div>
						<div className='form-group w50'>
							<TextField
								type='number'
								variant='outlined'
								label='Number of Columns:'
								name='columnCount'
								value={formData.columnCount}
								onChange={handleChange}
							/>
							{errors.columnCount && (
								<p className='error-msg'>{errors.columnCount}</p>
							)}
						</div>
					</div>
					<Button
						variant='outlined'
						className='generate-floor'
						onClick={generateFloor}
					>
						Add Floor
					</Button>
				</div>

				<FloorPlanBuilder
					floors={floorData?.floors || []}
					onModifyFloor={handleModifyRow}
					disabled={canAssignSeat}
				/>

				<div className='submit-btn-wrapper'>
					<Button
						variant='contained'
						onClick={handleSubmit}
						disabled={loadingBtn}
					>
						{loadingBtn ? (
							<>
								<i className='fa fa-spinner fa-spin' style={{ marginRight: '8px' }}></i>
								Updating...
							</>
						) : (
							'Update'
						)}
					</Button>
				</div>
			</div >
		</DashboardLayout >
	);
};

export default EditFloorPlan;
